import {
  Banner,
  Button,
  DataTable,
  DefinitionList,
  DetailLayout,
  ErrorState,
  FilterToolbar,
  PageHeader,
  SearchInput,
  Section,
  Select,
  Skeleton,
  SkeletonText,
  StatusBadge,
  TabPanel,
  Tabs,
  Truncate,
  useDebouncedText,
  useUrlFilters,
  type Column,
  type FilterItem,
} from "@flexfreight/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { api, ApiError } from "../../api/client";

type Money = { cents: number; currency: "USD" };
type Place = { city: string | null; region: string | null; postalCode: string | null };
export type ShipmentRow = {
  id: string;
  orgId: string;
  orgName: string;
  carrier: string;
  status: string;
  bolNumber: string | null;
  proNumber: string | null;
  origin: Place;
  destination: Place;
  pickupDate: string | null;
  estimatedDeliveryDate: string | null;
  deliveredAt: string | null;
  lastTrackedAt: string | null;
  lastEvent: string | null;
  needsAttention: boolean;
  price: Money;
  margin?: Money;
};
type Party = { company: string | null; contactName: string | null; phone: string | null; address: { line1: string | null; city: string | null; region: string | null; postalCode: string | null } };
export type ShipmentDetail = {
  shipment: Omit<ShipmentRow, "origin" | "destination" | "lastEvent" | "needsAttention"> & {
    pickupConfirmation: string | null;
    createdAt: string;
    bookedBy: string | null;
    shipper: Party;
    consignee: Party;
    units: { quantity: number | null; type: string | null; weightLbs: number | null; description: string | null; freightClass: string | null }[];
    accessorials: string[];
    cost?: Money;
  };
  events: { status: string | null; code: string; description: string; city: string | null; state: string | null; occurredAt: string; source: string }[];
  documents: { id: string; kind: string; contentType: string; sizeBytes: number; createdAt: string }[];
  showsCost: boolean;
};

const BUCKETS = ["attention", "active", "exception", "delivered", "cancelled", "all"] as const;
type Bucket = (typeof BUCKETS)[number];
export const CARRIERS = [
  ["xpo", "XPO"], ["odfl", "Old Dominion"], ["estes", "Estes"], ["saia", "Saia"], ["rl", "R+L"], ["abf", "ABF"], ["fedex_freight", "FedEx Freight"], ["tforce", "TForce"], ["amfast", "AmFast"], ["mock", "Mock"],
] as const;
export const carrierName = (c: string) => CARRIERS.find(([k]) => k === c)?.[1] ?? c.toUpperCase();
export const carrierOptions = (any = "Any") => [{ value: "", label: any }, ...CARRIERS.map(([value, l]) => ({ value, label: l }))];
const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
const money = (m: Money) => usd.format(m.cents / 100);
const dayFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" });
const timeFmt = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const day = (d: string | null) => (d ? dayFmt.format(new Date(d.length === 10 ? `${d}T12:00:00` : d)) : null);
const where = (p: Place | Party["address"]) => [p.city, p.region].filter(Boolean).join(", ") || p.postalCode || "Unknown";
const label = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const message = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Try again.");
const FILTERS = { bucket: "attention", q: "", carrier: "", org: "", page: 0 };
const PAGE_SIZE = 50;
const BUCKET_LABEL: Record<Bucket, string> = { attention: "Needs attention", active: "In motion", exception: "Exceptions", delivered: "Delivered", cancelled: "Cancelled", all: "All" };

/** Freight > Shipments: every shipment across customers, what needs attention first. Filters live in the URL. */
export function ShipmentsPage() {
  const f = useUrlFilters(FILTERS, useSearchParams());
  const v = f.values;
  const bucket: Bucket = (BUCKETS as readonly string[]).includes(v.bucket) ? (v.bucket as Bucket) : "attention";
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }));
  const navigate = useNavigate();
  const { search: listSearch } = useLocation();
  const query = new URLSearchParams({ bucket, q: v.q, carrier: v.carrier, page: String(v.page), ...(v.org ? { orgId: v.org } : {}) });
  const list = useQuery({
    queryKey: ["console", "shipments", query.toString()],
    queryFn: () => api<{ shipments: ShipmentRow[]; counts: { attention: number; active: number; exception: number }; showsCost: boolean; hasMore: boolean }>(`/v1/console/shipments?${query}`),
    placeholderData: keepPreviousData,
  });
  const rows = list.data?.shipments ?? [];
  const counts = list.data?.counts;
  const showsCost = list.data?.showsCost ?? false;
  const count = (b: Bucket) => (counts && (b === "attention" || b === "active" || b === "exception") ? ` (${counts[b]})` : "");
  const detail = (s: ShipmentRow) => `/shipments/${s.id}`;
  const items: FilterItem[] = [
    {
      key: "bucket",
      priority: 1,
      active: bucket !== "attention",
      inline: <Select variant="toolbar" label="Show" value={bucket} onChange={(b) => f.set({ bucket: b })} options={BUCKETS.map((b) => ({ value: b, label: `${BUCKET_LABEL[b]}${count(b)}` }))} />,
    },
    { key: "carrier", priority: 2, active: v.carrier !== "", inline: <Select variant="toolbar" label="Carrier" value={v.carrier} onChange={(carrier) => f.set({ carrier })} options={carrierOptions()} /> },
  ];
  const columns: Column<ShipmentRow>[] = [
    {
      key: "bol",
      header: "BOL",
      primary: true,
      width: "9.5rem",
      render: (s) => (
        <>
          <Link className="ffa-rowlink ffa-cell-strong ffx-num" to={detail(s)} state={{ from: listSearch }} aria-label={`Details for ${s.bolNumber ?? "shipment"}`}>
            {s.bolNumber ?? "Pending"}
          </Link>
          {s.proNumber ? <span className="ffa-muted ffa-block ffx-num">PRO {s.proNumber}</span> : null}
        </>
      ),
    },
    { key: "customer", header: "Customer", truncate: "custom", render: (s) => <Truncate text={s.orgName}><Link className="ffa-rowlink" to={`/customers/${s.orgId}`}>{s.orgName}</Link></Truncate> },
    { key: "lane", header: "Lane", render: (s) => `${where(s.origin)} → ${where(s.destination)}` },
    { key: "carrier", header: "Carrier", width: "8rem", mobile: "hide", render: (s) => carrierName(s.carrier) },
    { key: "status", header: "Status", width: "9.5rem", render: (s) => <StatusBadge domain="shipment" value={s.status} /> },
    { key: "latest", header: "Latest", truncate: true, mobile: "hide", text: (s) => s.lastEvent ?? "No tracking yet", render: (s) => s.lastEvent ?? <span className="ffa-muted">No tracking yet</span> },
    {
      key: "dates",
      header: "Pickup / ETA",
      width: "8.5rem",
      render: (s) => (
        <>
          {day(s.pickupDate) ?? "-"}
          <span className="ffa-muted ffa-block">{s.deliveredAt ? `Delivered ${day(s.deliveredAt)}` : s.estimatedDeliveryDate ? `ETA ${day(s.estimatedDeliveryDate)}` : "No ETA"}</span>
        </>
      ),
    },
    { key: "charge", header: "Charge", numeric: true, width: "7.5rem", render: (s) => money(s.price) },
    ...(showsCost ? [{ key: "margin", header: "Margin", numeric: true, width: "7rem", render: (s: ShipmentRow) => (s.margin ? money(s.margin) : "-") }] : []),
  ];
  const filtered = v.q !== "" || v.carrier !== "" || v.org !== "";
  return (
    <section className="ffa-page">
      <PageHeader title="Shipments" subtitle="Every shipment across customers. Exceptions, late and untracked loads come first." />
      <FilterToolbar
        label="Shipment filters"
        search={<SearchInput label="Search BOL, PRO, customer, company or ZIP" value={search} onChange={setSearch} />}
        items={items}
        anyActive={f.anyActive}
        showReset
        onReset={() => {
          setSearch("");
          f.reset();
        }}
      />
      {v.org ? (
        <Banner tone="info" actions={<Button size="sm" variant="ghost" onClick={() => f.set({ org: "" })}>Show all customers</Button>}>
          Showing one customer&apos;s shipments.
        </Banner>
      ) : null}
      <DataTable
        caption="Shipments"
        columns={columns}
        rows={rows}
        rowKey={(s) => s.id}
        loading={list.isPending || list.isFetching}
        error={list.isError ? { title: "Couldn't load shipments", message: message(list.error), onRetry: () => void list.refetch() } : null}
        empty={bucket === "attention" && !filtered ? "Nothing needs attention right now." : "No shipments match."}
        rowTone={(s) => (s.needsAttention ? "danger" : null)}
        onRowClick={(s) => navigate(detail(s), { state: { from: listSearch } })}
        rowHref={detail}
        pagination={{ page: v.page, pageSize: PAGE_SIZE, hasMore: !!list.data?.hasMore, onPageChange: (page) => f.set({ page }) }}
      />
    </section>
  );
}

function PartyBlock({ title, p }: { title: string; p: Party }) {
  return (
    <div className="ffx-cellstack">
      <h3 className="ffa-micro-title">{title}</h3>
      <p className="ffa-cell-strong">{p.company ?? "No company"}</p>
      <p className="ffa-small">{[p.address.line1, where(p.address), p.address.postalCode].filter(Boolean).join(" · ")}</p>
      {p.contactName || p.phone ? <p className="ffa-small ffa-muted">{[p.contactName, p.phone].filter(Boolean).join(" · ")}</p> : null}
    </div>
  );
}

type Unit = ShipmentDetail["shipment"]["units"][number] & { n: number };
type Doc = ShipmentDetail["documents"][number];
const TABS = ["overview", "tracking", "documents"] as const;
type Tab = (typeof TABS)[number];
const TAB_ID = "shipment";

/** Freight > Shipments > one shipment: DetailLayout with the facts and charges beside overview, tracking and documents. */
export function ShipmentPage() {
  const { shipmentId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const raw = params.get("tab");
  const tab: Tab = (TABS as readonly string[]).includes(raw ?? "") ? (raw as Tab) : "overview";
  const q = useQuery({ queryKey: ["console", "shipment", shipmentId], queryFn: () => api<ShipmentDetail>(`/v1/console/shipments/${shipmentId}`) });
  const from = (state as { from?: string } | null)?.from ?? "";
  const back = (
    <Link to={`/shipments${from}`} className="ffa-back">
      ← Shipments
    </Link>
  );
  if (q.isPending) {
    return (
      <section className="ffa-page" aria-busy="true">
        <DetailLayout
          header={<PageHeader back={back} eyebrow="Shipment" title={<Skeleton width="12rem" height="2.25rem" />} subtitle={<Skeleton width="18rem" />} />}
          aside={<Section title="Shipment"><SkeletonText lines={6} /></Section>}
        >
          <Section><SkeletonText lines={6} /></Section>
        </DetailLayout>
        <span className="ffx-sr">Loading shipment…</span>
      </section>
    );
  }
  if (q.isError) {
    const missing = q.error instanceof ApiError && q.error.status === 404;
    return (
      <section className="ffa-page">
        <PageHeader back={back} title={missing ? "Shipment not found" : "Shipment"} />
        {missing ? <Banner tone="warning" title="This shipment doesn't exist.">It may have been removed, or the link is wrong.</Banner> : <ErrorState title="Couldn't load this shipment" message={message(q.error)} onRetry={() => void q.refetch()} />}
      </section>
    );
  }
  const d = q.data;
  const s = d.shipment;
  const setTab = (t: Tab) => setParams((p) => { const n = new URLSearchParams(p); if (t === "overview") n.delete("tab"); else n.set("tab", t); return n; }, { replace: true, state });
  const latest = d.events[0];
  const unitCols: Column<Unit>[] = [
    { key: "qty", header: "Qty", numeric: true, width: "4.5rem", render: (u) => u.quantity ?? 1 },
    { key: "type", header: "Type", primary: true, width: "7rem", render: (u) => <span className="ffa-cap">{u.type ?? "unit"}</span> },
    { key: "desc", header: "Description", truncate: true, text: (u) => u.description ?? "-", render: (u) => u.description ?? "-" },
    { key: "class", header: "Class", width: "5rem", render: (u) => u.freightClass ?? "-" },
    { key: "weight", header: "Weight", numeric: true, width: "7rem", render: (u) => (u.weightLbs ? `${u.weightLbs.toLocaleString("en-US")} lb` : "-") },
  ];
  const docCols: Column<Doc>[] = [
    { key: "kind", header: "Document", primary: true, render: (doc) => <a href={`/v1/console/shipments/${s.id}/documents/${doc.id}`} target="_blank" rel="noopener noreferrer">{label(doc.kind)}</a> },
    { key: "added", header: "Added", width: "9rem", render: (doc) => day(doc.createdAt) },
    { key: "size", header: "Size", numeric: true, width: "6rem", render: (doc) => `${Math.max(1, Math.round(doc.sizeBytes / 1024))} KB` },
  ];
  return (
    <section className="ffa-page">
      <DetailLayout
        header={
          <PageHeader
            back={back}
            eyebrow="Shipment"
            title={s.bolNumber ?? "Shipment"}
            badges={<StatusBadge domain="shipment" value={s.status} />}
            subtitle={`${carrierName(s.carrier)} for ${s.orgName}. Booked ${timeFmt.format(new Date(s.createdAt))}${s.bookedBy ? ` by ${s.bookedBy}` : ""}.`}
            primaryAction={<Link className="ffx-btn" to={`/customers/${s.orgId}`}>Open customer</Link>}
          />
        }
        tabs={
          <Tabs
            idBase={TAB_ID}
            label="Shipment sections"
            value={tab}
            onChange={setTab}
            tabs={[
              { value: "overview", label: "Overview" },
              { value: "tracking", label: "Tracking", count: d.events.length },
              { value: "documents", label: "Documents", count: d.documents.length },
            ]}
          />
        }
        aside={
          <>
            <Section title="Shipment">
              <DefinitionList
                items={[
                  { term: "Status", detail: <StatusBadge domain="shipment" value={s.status} /> },
                  { term: "Carrier", detail: carrierName(s.carrier) },
                  { term: "PRO", detail: s.proNumber ?? "Not yet" },
                  s.pickupConfirmation ? { term: "Pickup #", detail: s.pickupConfirmation } : null,
                  { term: "Pickup", detail: day(s.pickupDate) ?? "Not set" },
                  s.deliveredAt ? { term: "Delivered", detail: day(s.deliveredAt) } : { term: "ETA", detail: day(s.estimatedDeliveryDate) ?? "Not given" },
                  { term: "Latest", detail: latest ? latest.description : "No tracking yet" },
                  { term: "Customer", detail: <Link to={`/customers/${s.orgId}`}>{s.orgName}</Link> },
                ]}
              />
            </Section>
            <Section title="Charges">
              <DefinitionList
                items={[
                  { term: "Customer charge", detail: <span className="ffx-num">{money(s.price)}</span> },
                  s.cost ? { term: "Carrier cost", detail: <span className="ffx-num">{money(s.cost)}</span> } : null,
                  s.margin ? { term: "Margin", detail: <span className="ffx-num">{money(s.margin)} ({s.price.cents ? Math.round((s.margin.cents / s.price.cents) * 1000) / 10 : 0}%)</span> } : null,
                ]}
              />
            </Section>
          </>
        }
        asideLabel="Shipment facts"
      >
        <TabPanel idBase={TAB_ID} value={tab}>
          {tab === "overview" ? (
            <>
              <Section title="Pickup and delivery">
                <div className="ffx-fields">
                  <PartyBlock title="Shipper" p={s.shipper} />
                  <PartyBlock title="Consignee" p={s.consignee} />
                </div>
              </Section>
              <Section title="Freight" description={s.accessorials.length ? `Extras: ${s.accessorials.map(label).join(", ")}` : "No extra services."} flush>
                <DataTable caption="Freight" columns={unitCols} rows={s.units.map((u, n) => ({ ...u, n }))} rowKey={(u) => String(u.n)} empty="No freight lines recorded." maxHeight="none" density="compact" />
              </Section>
            </>
          ) : null}
          {tab === "tracking" ? (
            <Section title="Tracking" description="Newest first, from the carrier and from staff.">
              {d.events.length === 0 ? (
                <p className="ffa-muted">No tracking events yet.</p>
              ) : (
                <ol className="ffa-timeline">
                  {d.events.map((e, i) => (
                    <li key={`${e.occurredAt}-${i}`}>
                      <time dateTime={e.occurredAt} className="ffa-muted">{timeFmt.format(new Date(e.occurredAt))}</time>
                      <span>
                        {e.description}
                        {e.city ? <span className="ffa-muted"> · {[e.city, e.state].filter(Boolean).join(", ")}</span> : null}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </Section>
          ) : null}
          {tab === "documents" ? (
            <Section title="Documents" flush>
              <DataTable caption="Documents" columns={docCols} rows={d.documents} rowKey={(doc) => doc.id} empty="No documents yet." maxHeight="none" />
            </Section>
          ) : null}
        </TabPanel>
      </DetailLayout>
    </section>
  );
}
